import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AuthProvider, { useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Navbar from "./components/Navbar";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import ChatPage from "./pages/ChatPage";
import EditAssistantPage from "./pages/EditAssistantPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ProfileSetupPage from "./pages/ProfileSetupPage";
import FeedPage from "./pages/FeedPage";
import UserProfilePage from "./pages/UserProfilePage";
import MarketplacePage from "./pages/MarketplacePage";
import MarketplaceItemPage from "./pages/MarketplaceItemPage";
import ExplorePage from "./pages/ExplorePage";
import NotificationsPage from "./pages/NotificationsPage";
import PrivacySettingsPage from "./pages/PrivacySettingsPage";
import SecuritySettingsPage from "./pages/SecuritySettingsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import RelationshipsPage from "./pages/RelationshipsPage";
import AchievementsPage from "./pages/AchievementsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import MarketplaceCreatePage from "./pages/MarketplaceCreatePage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import CreatorStorefrontPage from "./pages/CreatorStorefrontPage";
import CreatorsDirectoryPage from "./pages/CreatorsDirectoryPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import SocialAccountsPage from "./pages/SocialAccountsPage";
import PublishPage from "./pages/PublishPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AdminModerationPage from "./pages/AdminModerationPage";
import NotFoundPage from "./pages/NotFoundPage";
import { Toaster } from "./components/ui/sonner";
import "@/App.css";

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function ProfileRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/profile/${user.username || user.name || "me"}`} replace />;
}

function AppRoutes() {
  // CRITICAL: Check URL fragment for session_id synchronously during render.
  // This prevents race conditions by processing OAuth callback FIRST.
  const hash = window.location.hash;
  if (hash?.includes('session_id=') || hash?.includes('session_token=')) {
    return <AuthCallbackPage />;
  }

  return (
    <>
      <Navbar />
      <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route path="/profile/setup" element={<ProtectedRoute><ProfileSetupPage /></ProtectedRoute>} />
            <Route path="/profile/:username" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            <Route path="/assistant/:id/edit" element={<ProtectedRoute><EditAssistantPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
            <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettingsPage /></ProtectedRoute>} />
            <Route path="/settings/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/relationships" element={<ProtectedRoute><RelationshipsPage /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
            <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
            <Route path="/marketplace/create" element={<ProtectedRoute><MarketplaceCreatePage /></ProtectedRoute>} />
            <Route path="/marketplace/dashboard" element={<ProtectedRoute><CreatorDashboardPage /></ProtectedRoute>} />
            <Route path="/marketplace/:id" element={<ProtectedRoute><MarketplaceItemPage /></ProtectedRoute>} />
            <Route path="/creators" element={<ProtectedRoute><CreatorsDirectoryPage /></ProtectedRoute>} />
            <Route path="/creators/:username" element={<ProtectedRoute><CreatorStorefrontPage /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
            <Route path="/settings/social-accounts" element={<ProtectedRoute><SocialAccountsPage /></ProtectedRoute>} />
            <Route path="/publish" element={<ProtectedRoute><PublishPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/admin/moderation" element={<ProtectedRoute><AdminModerationPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileRedirect /></ProtectedRoute>} />
            <Route path="/me" element={<ProtectedRoute><ProfileRedirect /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><ProfileRedirect /></ProtectedRoute>} />
            <Route path="/settings" element={<Navigate to="/settings/privacy" replace />} />
            <Route path="/chat/:slug" element={<ChatPage />} />
            <Route path="/:slug" element={<ChatPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </>
  );
}

export default App;
